import React, { useState } from 'react';
import { NotionBlockRenderer } from './shared';
import {
  ChevronLeft, ChevronRight, ArrowLeft, AlertTriangle,
  List, CheckCircle2, MessageSquare, BookOpen, Database
} from 'lucide-react';

// Situation colour map
var SIT_COLORS = {
  coral:  { bg: 'hsl(14 55% 95%)',  border: 'hsl(14 55% 55%)',  num: 'hsl(14 55% 50%)',  text: 'hsl(14 55% 28%)' },
  amber:  { bg: 'hsl(38 80% 95%)',  border: 'hsl(38 80% 50%)',  num: 'hsl(38 80% 42%)',  text: 'hsl(38 70% 24%)' },
  teal:   { bg: 'hsl(185 40% 95%)', border: 'hsl(185 55% 42%)', num: 'hsl(185 55% 38%)', text: 'hsl(185 55% 22%)' },
  grey:   { bg: 'hsl(220 10% 95%)', border: 'hsl(220 10% 65%)', num: 'hsl(220 10% 50%)', text: 'hsl(220 10% 30%)' },
};

function getSitColor(idx) {
  if (idx <= 3) return SIT_COLORS.teal;    // Sit 1-4: teal
  if (idx === 4) return SIT_COLORS.amber;  // Sit 5: amber (keep current)
  return SIT_COLORS.grey;                   // Sit 6-8: grey
}

export function GuidedProcedureView({ guided, blocks }) {
  if (!guided || !guided.pattern || guided.pattern === 'simple') {
    return React.createElement(FullTextView, { guided: guided, blocks: blocks });
  }
  if (guided.pattern === 'branched') {
    return React.createElement(BranchedView, { guided: guided, blocks: blocks });
  }
  if (guided.pattern === 'sequential') {
    return React.createElement(SequentialView, { guided: guided, blocks: blocks });
  }
  if (guided.pattern === 'sectioned') {
    return React.createElement(SectionedView, { guided: guided, blocks: blocks });
  }
  return React.createElement(FullTextView, { guided: guided, blocks: blocks });
}

// ============================================================
// BRANCHED VIEW — 4×2 grid, colour coded, CRM action detection
// ============================================================
function BranchedView({ guided, blocks }) {
  var _s1 = useState(null), selectedBranch = _s1[0], setSelectedBranch = _s1[1];
  var _s2 = useState(0), currentStep = _s2[0], setCurrentStep = _s2[1];
  var _s3 = useState(false), showFullText = _s3[0], setShowFullText = _s3[1];

  // Situation selector — 4×2 grid
  if (selectedBranch === null) {
    return React.createElement('div', { className: 'guided-view', 'data-testid': 'guided-branched' },
      guided.trigger ? React.createElement('div', { className: 'guided-trigger' },
        React.createElement('p', { className: 'guided-trigger-label' }, 'When to use this'),
        React.createElement('p', { className: 'guided-trigger-text' }, guided.trigger)
      ) : null,

      React.createElement('div', { className: 'guided-question' },
        React.createElement('div', { className: 'guided-question-icon' }, React.createElement(MessageSquare, { size: 28 })),
        React.createElement('h3', { className: 'guided-question-text' }, guided.initial_question || 'Which situation are you in?'),
        React.createElement('p', { className: 'guided-question-hint' }, 'Select the option that matches your current situation')
      ),

      // 4×2 grid
      React.createElement('div', { className: 'sit-grid' },
        guided.segments.map(function(seg, idx) {
          var option = guided.initial_options[idx] || {};
          var color = getSitColor(idx);
          return React.createElement('button', {
            key: idx,
            className: 'sit-card',
            style: { background: color.bg, borderColor: color.border },
            onClick: function() { setSelectedBranch(idx); setCurrentStep(0); },
            'data-testid': 'guided-branch-option-' + idx
          },
            React.createElement('div', { className: 'sit-card-num', style: { background: color.num } }, idx + 1),
            React.createElement('div', { className: 'sit-card-body' },
              React.createElement('p', { className: 'sit-card-label', style: { color: color.text } }, option.label || seg.title),
              React.createElement('p', { className: 'sit-card-desc' }, option.description || '')
            )
          );
        })
      ),

      React.createElement('button', { className: 'guided-full-toggle', onClick: function() { setShowFullText(!showFullText); } },
        showFullText ? 'Hide full procedure' : 'Show full procedure text'
      ),
      showFullText ? React.createElement('div', { className: 'guided-full-text' }, React.createElement(NotionBlockRenderer, { blocks: blocks })) : null
    );
  }

  // Branch selected — parse into steps
  var segment = guided.segments[selectedBranch];
  if (!segment) return null;
  var sitColor = getSitColor(selectedBranch);

  var steps = [];
  var curTitle = '';
  var curBlocks = [];
  var curIsCRM = false;
  var escalation = [];
  var inEsc = false;

  segment.blocks.forEach(function(block) {
    var plain = (block.plain_text || '').trim();
    var upper = plain.toUpperCase();
    if (upper.startsWith('ESCALAT') || upper.indexOf('ESCALATE TO DIRECTOR') >= 0) { inEsc = true; escalation.push(block); return; }
    if (inEsc) { escalation.push(block); return; }

    if (upper.match(/^STEP\s*\d/)) {
      if (curTitle || curBlocks.length > 0) {
        steps.push({ title: curTitle, blocks: curBlocks, isCRM: curIsCRM });
      }
      curTitle = plain.replace(/^Step\s*\d+\s*[\u2013\u2014\-—]\s*/i, '').trim();
      curIsCRM = upper.indexOf('CRM') >= 0;
      curBlocks = [];
    } else {
      curBlocks.push(block);
    }
  });
  if (curTitle || curBlocks.length > 0) {
    steps.push({ title: curTitle, blocks: curBlocks, isCRM: curIsCRM });
  }
  if (guided.escalation && guided.escalation.length > 0 && escalation.length === 0) {
    escalation = guided.escalation;
  }

  var hasSteps = steps.length > 1;
  var total = steps.length;

  // No steps — simple card
  if (!hasSteps) {
    return React.createElement('div', { className: 'guided-view', 'data-testid': 'guided-branch-detail' },
      React.createElement('button', { className: 'guided-back-btn', onClick: function() { setSelectedBranch(null); }, 'data-testid': 'guided-back-button' },
        React.createElement(ArrowLeft, { size: 18 }), React.createElement('span', null, 'Back to situations')
      ),
      React.createElement('h3', { className: 'guided-branch-title' }, segment.title),
      React.createElement('div', { className: 'branch-simple-content' }, React.createElement(NotionBlockRenderer, { blocks: segment.blocks })),
      escalation.length > 0 ? React.createElement('div', { className: 'branch-escalation-strip' },
        React.createElement(AlertTriangle, { size: 14 }), React.createElement('div', null, React.createElement(NotionBlockRenderer, { blocks: escalation }))
      ) : null
    );
  }

  // Step-by-step navigator
  var active = steps[currentStep] || steps[0];

  return React.createElement('div', { className: 'guided-view', 'data-testid': 'guided-branch-detail' },
    React.createElement('button', { className: 'guided-back-btn', onClick: function() { setSelectedBranch(null); }, 'data-testid': 'guided-back-button' },
      React.createElement(ArrowLeft, { size: 18 }), React.createElement('span', null, 'Back to situations')
    ),

    // Progress
    React.createElement('div', { className: 'branch-progress' },
      React.createElement('div', { className: 'branch-progress-bar' },
        React.createElement('div', { className: 'branch-progress-fill', style: { width: ((currentStep + 1) / total * 100) + '%', background: sitColor.border } })
      ),
      React.createElement('span', { className: 'branch-progress-label', style: { color: sitColor.num } }, 'Step ' + (currentStep + 1) + ' of ' + total)
    ),

    // Dots
    React.createElement('div', { className: 'branch-step-dots' },
      steps.map(function(s, idx) {
        var dotStyle = {};
        if (idx === currentStep) { dotStyle = { background: sitColor.border, borderColor: sitColor.border }; }
        else if (idx < currentStep) { dotStyle = { background: sitColor.bg, borderColor: sitColor.border }; }
        return React.createElement('button', { key: idx, className: 'branch-dot' + (idx === currentStep ? ' active' : '') + (idx < currentStep ? ' past' : ''), style: dotStyle, onClick: function() { setCurrentStep(idx); } });
      })
    ),

    // Step card — CRM steps get amber treatment
    active.isCRM ?
      React.createElement('div', { className: 'branch-crm-card' },
        React.createElement('div', { className: 'branch-crm-header' },
          React.createElement(Database, { size: 16 }),
          React.createElement('span', null, 'CRM Action')
        ),
        React.createElement('div', { className: 'branch-crm-content' },
          React.createElement(NotionBlockRenderer, { blocks: active.blocks })
        )
      )
    :
      React.createElement('div', { className: 'branch-step-card', style: { borderColor: sitColor.bg } },
        active.title ? React.createElement('div', { className: 'branch-step-title', style: { background: sitColor.bg, borderColor: sitColor.bg } },
          React.createElement('span', { className: 'branch-step-num', style: { background: sitColor.num } }, currentStep + 1),
          React.createElement('span', { style: { color: sitColor.text } }, active.title)
        ) : null,
        React.createElement('div', { className: 'branch-step-content sit-quote-' + (selectedBranch <= 3 ? 'teal' : selectedBranch === 4 ? 'amber' : 'grey') },
          React.createElement(NotionBlockRenderer, { blocks: active.blocks })
        )
      ),

    // Nav
    React.createElement('div', { className: 'branch-nav' },
      currentStep > 0 ? React.createElement('button', { className: 'branch-nav-btn prev', onClick: function() { setCurrentStep(currentStep - 1); } },
        React.createElement(ChevronLeft, { size: 18 }), React.createElement('span', null, 'Previous')
      ) : React.createElement('div', null),
      currentStep < total - 1 ? React.createElement('button', { className: 'branch-nav-btn next', style: { background: sitColor.border, borderColor: sitColor.border }, onClick: function() { setCurrentStep(currentStep + 1); } },
        React.createElement('span', null, 'Next'), React.createElement(ChevronRight, { size: 18 })
      ) : React.createElement('button', { className: 'branch-nav-btn done', onClick: function() { setSelectedBranch(null); } },
        React.createElement(CheckCircle2, { size: 18 }), React.createElement('span', null, 'Done')
      )
    ),

    escalation.length > 0 ? React.createElement('div', { className: 'branch-escalation-strip' },
      React.createElement(AlertTriangle, { size: 14 }), React.createElement('div', null, React.createElement(NotionBlockRenderer, { blocks: escalation }))
    ) : null
  );
}

// ============================================================
// SEQUENTIAL VIEW
// ============================================================
function SequentialView({ guided, blocks }) {
  var _s1 = useState(0), activeStage = _s1[0], setActiveStage = _s1[1];
  var _s2 = useState(false), showFullText = _s2[0], setShowFullText = _s2[1];
  var totalStages = guided.segments.length;
  var segment = guided.segments[activeStage];

  return React.createElement('div', { className: 'guided-view', 'data-testid': 'guided-sequential' },
    guided.trigger ? React.createElement('div', { className: 'guided-trigger' },
      React.createElement('p', { className: 'guided-trigger-label' }, 'When to use this'),
      React.createElement('p', { className: 'guided-trigger-text' }, guided.trigger)
    ) : null,
    React.createElement('div', { className: 'guided-progress' },
      React.createElement('div', { className: 'guided-progress-bar' },
        React.createElement('div', { className: 'guided-progress-fill', style: { width: ((activeStage + 1) / totalStages * 100) + '%' } })
      ),
      React.createElement('span', { className: 'guided-progress-label' }, (activeStage + 1) + ' of ' + totalStages)
    ),
    React.createElement('div', { className: 'guided-stage-nav' },
      guided.segments.map(function(seg, idx) {
        return React.createElement('button', { key: idx, className: 'guided-stage-pill ' + (idx === activeStage ? 'active' : '') + (idx < activeStage ? ' past' : ''), onClick: function() { setActiveStage(idx); }, title: seg.title },
          React.createElement('span', { className: 'guided-stage-pill-num' }, idx + 1)
        );
      })
    ),
    segment ? React.createElement('div', null,
      React.createElement('h3', { className: 'guided-stage-title' }, segment.title),
      React.createElement('div', { className: 'guided-stats-row' },
        segment.stats.checklists > 0 ? React.createElement('span', { className: 'guided-stat' }, React.createElement(CheckCircle2, { size: 14 }), segment.stats.checklists + ' checks') : null,
        segment.stats.scripts > 0 ? React.createElement('span', { className: 'guided-stat' }, React.createElement(MessageSquare, { size: 14 }), segment.stats.scripts + ' scripts') : null
      ),
      React.createElement('div', { className: 'guided-segment-content' }, React.createElement(NotionBlockRenderer, { blocks: segment.blocks }))
    ) : null,
    React.createElement('div', { className: 'guided-nav-buttons' },
      activeStage > 0 ? React.createElement('button', { className: 'guided-nav-btn prev', onClick: function() { setActiveStage(activeStage - 1); } },
        React.createElement(ChevronLeft, { size: 18 }), React.createElement('span', null, 'Previous')
      ) : React.createElement('div', null),
      activeStage < totalStages - 1 ? React.createElement('button', { className: 'guided-nav-btn next', onClick: function() { setActiveStage(activeStage + 1); } },
        React.createElement('span', null, 'Next'), React.createElement(ChevronRight, { size: 18 })
      ) : null
    ),
    React.createElement('button', { className: 'guided-full-toggle', onClick: function() { setShowFullText(!showFullText); } },
      showFullText ? 'Hide full procedure' : 'Show full procedure text'
    ),
    showFullText ? React.createElement('div', { className: 'guided-full-text' }, React.createElement(NotionBlockRenderer, { blocks: blocks })) : null
  );
}

// ============================================================
// SECTIONED VIEW
// ============================================================
function SectionedView({ guided, blocks }) {
  var _s1 = useState(null), activeSection = _s1[0], setActiveSection = _s1[1];
  var _s2 = useState(false), showFullText = _s2[0], setShowFullText = _s2[1];

  return React.createElement('div', { className: 'guided-view', 'data-testid': 'guided-sectioned' },
    guided.trigger ? React.createElement('div', { className: 'guided-trigger' },
      React.createElement('p', { className: 'guided-trigger-label' }, 'When to use this'),
      React.createElement('p', { className: 'guided-trigger-text' }, guided.trigger)
    ) : null,
    guided.purpose ? React.createElement('div', { className: 'guided-purpose' },
      React.createElement('p', null, guided.purpose)
    ) : null,
    React.createElement('div', { className: 'guided-question' },
      React.createElement('div', { className: 'guided-question-icon' }, React.createElement(BookOpen, { size: 24 })),
      React.createElement('h3', { className: 'guided-question-text' }, 'What are you looking for?'),
      React.createElement('p', { className: 'guided-question-hint' }, 'Select a section to view its content')
    ),
    React.createElement('div', { className: 'guided-sections-list' },
      guided.segments.map(function(seg, idx) {
        var isActive = activeSection === idx;
        return React.createElement('div', { key: idx },
          React.createElement('button', { className: 'guided-section-item ' + (isActive ? 'active' : ''), onClick: function() { setActiveSection(isActive ? null : idx); }, 'data-testid': 'guided-section-' + idx },
            React.createElement('span', { className: 'guided-section-num' }, idx + 1),
            React.createElement('span', { className: 'guided-section-title' }, seg.title.replace(/^SECTION\s*\d+\s*[\u2013\u2014-]\s*/i, '').trim() || seg.title),
            React.createElement('div', { className: 'guided-section-meta' },
              seg.stats.checklists > 0 ? React.createElement('span', { className: 'guided-stat-mini' }, seg.stats.checklists + ' checks') : null,
              seg.stats.scripts > 0 ? React.createElement('span', { className: 'guided-stat-mini' }, seg.stats.scripts + ' scripts') : null
            ),
            React.createElement(isActive ? ChevronLeft : ChevronRight, { size: 16, className: 'guided-section-arrow', style: { transform: isActive ? 'rotate(-90deg)' : 'none' } })
          ),
          isActive ? React.createElement('div', { className: 'guided-section-content' }, React.createElement(NotionBlockRenderer, { blocks: seg.blocks })) : null
        );
      })
    ),
    React.createElement('button', { className: 'guided-full-toggle', onClick: function() { setShowFullText(!showFullText); } },
      showFullText ? 'Hide full procedure' : 'Show full procedure text'
    ),
    showFullText ? React.createElement('div', { className: 'guided-full-text' }, React.createElement(NotionBlockRenderer, { blocks: blocks })) : null
  );
}

// ============================================================
// FULL TEXT VIEW
// ============================================================
function FullTextView({ guided, blocks }) {
  return React.createElement('div', { className: 'guided-view' },
    guided && guided.trigger ? React.createElement('div', { className: 'guided-trigger' },
      React.createElement('p', { className: 'guided-trigger-label' }, 'When to use this'),
      React.createElement('p', { className: 'guided-trigger-text' }, guided.trigger)
    ) : null,
    guided && guided.purpose ? React.createElement('div', { className: 'guided-purpose' },
      React.createElement('p', null, guided.purpose)
    ) : null,
    React.createElement(NotionBlockRenderer, { blocks: blocks })
  );
}
